import React from "react";
//#region types
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { CommonEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  IconTitle,
  GenericCommentsView,
  Icon,
  PageLayouts,
  RouteBreadCrumb,
  CreditRating,
  StyledButton,
  useMountedState,
  ViewPanel,
  ProfileImage,
  MasonryPanel,
  WidgetArea,
  ChartContextProvider,
  ChartToggle,
} from "components/common";
import SupplierInvoiceList from "./invoiceList";
import SupplierRulesList from "./rulesList";
import SupplierSimpleTodoCard from "./simpleTodoCard";
import InvoiceTotalByCategoryChart from "./widgets/totalByCategory";
import SupplierDetailsList from "./detailsList";
import { Grid, Skeleton } from "@mui/material";
import { useSupplierService } from "serviceHooks";
import useSupplierPermissions from "./usePermissions";
import { InvoiceTotalByMonthChart } from "./widgets";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import cssExports from "theme/styles/main.module.scss";
import { TextSkeleton } from "components/common/skeletons";
import clsx from "clsx";

const SupplierProfile: React.FC = () => {
  const supplierService = useSupplierService();
  const { getMounted } = useMountedState();
  const urlParams = useParams<EntityIdParams>();
  const id = Number.parseInt(urlParams.id ?? "0");
  const location = useLocation();
  const navigate = useNavigate();

  const { canViewSuppliers } = useSupplierPermissions();

  const [supplier, setSupplier] = React.useState<ApiModel.IncomingInvoiceSupplierInfo>();

  const getSupplier = () => {
    supplierService
      .getSupplier(id)
      .then((result) => {
        if (getMounted()) {
          setSupplier(result);
        }
      })
      .catch((e) => {
        console.warn("error");
      });
  };

  React.useEffect(() => {
    getSupplier();
  }, [id]);

  const onSettingsClick = () => {
    navigate(UrlHelper.formatPath(`/Supplier/${id}/Settings`), {
      state: { background: location },
    });
  };

  const topRightButtons = (
    <DashboardButtonGroup
      buttons={[
        <StyledButton size="sm" onClick={onSettingsClick} className={cssExports.iconButton}>
          <Icon icon={IconDefinitionConstants.WSettings} />
        </StyledButton>,
      ]}
    />
  );

  const onClickEditRules = () => {
    navigate(UrlHelper.formatPath(`/Supplier/${id}/Settings`), {
      state: { background: location },
    });
  };

  return (
    <PageLayouts.DashboardLayout
      entityKey={id}
      hasPermissions={canViewSuppliers}
      title={
        supplier ? (
          <IconTitle
            title={supplier?.name ?? ""}
            subtitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.Supplier)}
            icon={<ProfileImage id={0} name={supplier?.name ?? ""} />}
            defaultTagSurface="background"
          />
        ) : (
          <Grid container spacing={2} className={cssExports.iconTitleContainer}>
            <Grid item className={cssExports.dashboardHeaderIcon}>
              <Skeleton variant="circular" className={clsx(cssExports.supplierAvatar)} />
            </Grid>
            <Grid item xs container direction={"column"}>
              <Grid item className={cssExports.dashboardHeaderText}>
                <Skeleton variant="text" sx={{ fontSize: "8rem" }} width="40%" />
              </Grid>
              <Grid item container direction="row" spacing={1}>
                <Grid item>
                  <Skeleton variant="text" sx={{ fontSize: "2rem" }} width={80} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )
      }
      topRightButtons={topRightButtons}
      breadCrumb={<RouteBreadCrumb className={cssExports.dashBoardNavigation} current={supplier?.name ?? ""} />}
    >
      <MasonryPanel>
        {supplier ? (
          <SupplierDetailsList supplier={supplier} />
        ) : (
          <Grid container className={clsx(cssExports.detailsTableView)}>
            <TextSkeleton lines={7} />
          </Grid>
        )}
        <SupplierSimpleTodoCard supplierId={id} />
        <WidgetArea>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Last12Months)}
            appearance={"sm"}
            gridSize="md"
          >
            <InvoiceTotalByCategoryChart supplierId={id} supplierName={supplier?.name ?? ""} />
          </ViewPanel>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CreditRating)}
            appearance={"sm"}
            gridSize="md"
          >
            {supplier ? (
              <CreditRating orgNr={supplier.orgNr} orgType={CommonEnums.OrganizationTypes.Supplier} />
            ) : (
              <></>
            )}
          </ViewPanel>
        </WidgetArea>
        <WidgetArea>
          <ChartContextProvider>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InvoicedAmount)}
              appearance="sm"
              topRightContent={<ChartToggle />}
            >
              <InvoiceTotalByMonthChart supplierId={id} supplierName={supplier?.name ?? ""} />
            </ViewPanel>
          </ChartContextProvider>
        </WidgetArea>
      </MasonryPanel>
      <WidgetArea>
        <ViewPanel
          icon={IconDefinitionConstants.WBolt}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AutoApprovals)}
          topRightContent={
            <StyledButton onClick={onClickEditRules} size="xs">
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)}
            </StyledButton>
          }
        >
          <SupplierRulesList supplierId={id} />
        </ViewPanel>
        <ViewPanel
          icon={IconDefinitionConstants.WEnvelop}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AllInvoices)}
        >
          <SupplierInvoiceList supplierId={id} />
        </ViewPanel>
      </WidgetArea>
      <Grid item xs={12}>
        <GenericCommentsView parentId={id} type={ApiModel.GenericCommentType.Supplier} />
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default SupplierProfile;
